import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, signInWithEmailAndPassword, signInWithPopup, createUserWithEmailAndPassword, signOut, type Auth } from 'firebase/auth'
import { getFirestore, doc, setDoc, type Firestore } from 'firebase/firestore'
import { getDatabase, limitToLast, onDisconnect, onValue, query, ref as databaseRef, remove, serverTimestamp, set, type Database } from 'firebase/database'
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes, type FirebaseStorage } from 'firebase/storage'
import type { Avatar, GamePlayerSnapshot, Inventory, PlayerProfile } from '../types'

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
}

export const isFirebaseConfigured = Boolean(config.apiKey && config.authDomain && config.projectId && config.appId)

let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let realtime: Database | null = null
let storage: FirebaseStorage | null = null

if (isFirebaseConfigured) {
  app = getApps()[0] ?? initializeApp(config)
  auth = getAuth(app)
  db = getFirestore(app)
  realtime = getDatabase(app)
  storage = getStorage(app)
}

export { app, auth, db, realtime, storage }

export async function loginWithEmail(email: string, password: string) {
  if (!auth) throw new Error('Firebase is not configured. Use demo mode or add VITE_FIREBASE_* values.')
  return signInWithEmailAndPassword(auth, email, password)
}

export async function registerWithEmail(email: string, password: string) {
  if (!auth) throw new Error('Firebase is not configured. Use demo mode or add VITE_FIREBASE_* values.')
  return createUserWithEmailAndPassword(auth, email, password)
}

export async function loginWithGoogle() {
  if (!auth) throw new Error('Firebase is not configured. Use demo mode or add VITE_FIREBASE_* values.')
  return signInWithPopup(auth, new GoogleAuthProvider())
}

export async function logoutFirebase() {
  if (auth) await signOut(auth)
}

export async function syncProfileToFirebase(user: PlayerProfile) {
  if (!db) return
  await setDoc(doc(db, 'users', user.id), user, { merge: true })
}

export async function syncAvatarToFirebase(userId: string, avatar: Avatar) {
  if (!db) return
  await setDoc(doc(db, 'avatars', userId), avatar, { merge: true })
}

export async function syncInventoryToFirebase(inventory: Inventory) {
  if (!db) return
  await setDoc(doc(db, 'inventory', inventory.userId), inventory, { merge: true })
}

export async function uploadGameThumbnail(userId: string, file: File) {
  if (!storage) throw new Error('Firebase Storage is not configured. Use the local thumbnail preview in demo mode.')
  const target = storageRef(storage, `game-thumbnails/${userId}/${Date.now()}-${file.name}`)
  const uploaded = await uploadBytes(target, file)
  return getDownloadURL(uploaded.ref)
}

export async function savePlayerSnapshot(gameId: string, snapshot: GamePlayerSnapshot) {
  if (!realtime) return
  const playerRef = databaseRef(realtime, `gamePlayers/${gameId}/${snapshot.playerId}`)
  await onDisconnect(playerRef).remove()
  await set(playerRef, { ...snapshot, updatedAt: serverTimestamp() })
}

export function subscribeToPlayers(gameId: string, onPlayers: (players: GamePlayerSnapshot[]) => void) {
  if (!realtime) return () => undefined
  const players = query(databaseRef(realtime, `gamePlayers/${gameId}`), limitToLast(32))
  return onValue(players, (snapshot) => {
    const value = snapshot.val() as Record<string, GamePlayerSnapshot> | null
    onPlayers(Object.values(value ?? {}))
  })
}

export async function removePlayerSnapshot(gameId: string, playerId: string) {
  if (!realtime) return
  await remove(databaseRef(realtime, `gamePlayers/${gameId}/${playerId}`))
}
